
import { useState } from "react";
import { ShoppingBag, Star, Truck, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface ProductInfoProps {
  product: {
    name: string;
    rating: number;
    reviewCount: number;
    price: number;
    description: string;
    tags: string[];
    inStock: boolean;
  };
}

const ProductInfo = ({ product }: ProductInfoProps) => {
  const [quantity, setQuantity] = useState(1);

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const increaseQuantity = () => {
    setQuantity(quantity + 1);
  };

  const addToCart = () => {
    toast.success(`Added ${quantity} ${product.name} to your cart`);
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
      <div className="flex items-center mb-4">
        <div className="flex text-yellow-400 mr-2">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              fill={i < Math.floor(product.rating) ? "currentColor" : "none"}
              className="h-4 w-4"
            />
          ))}
        </div>
        <span className="text-sm text-muted-foreground">
          {product.rating} ({product.reviewCount} reviews)
        </span>
      </div>
      <div className="text-2xl font-semibold mb-6">${product.price.toFixed(2)}</div>
      
      <p className="text-muted-foreground mb-6">{product.description}</p>
      
      <div className="mb-6">
        <div className="font-medium mb-2">Tags:</div>
        <div className="flex flex-wrap gap-2">
          {product.tags.map((tag) => (
            <span key={tag} className="px-3 py-1 bg-muted rounded-full text-sm">
              {tag}
            </span>
          ))}
        </div>
      </div>

      {product.inStock ? (
        <div className="flex items-center text-green-600 mb-6">
          <div className="w-3 h-3 rounded-full bg-green-600 mr-2"></div>
          In Stock
        </div>
      ) : (
        <div className="flex items-center text-destructive mb-6">
          <div className="w-3 h-3 rounded-full bg-destructive mr-2"></div>
          Out of Stock
        </div>
      )}

      {/* Quantity Selector */}
      <div className="flex items-center mb-6">
        <span className="mr-4 font-medium">Quantity:</span>
        <div className="flex items-center border border-input rounded-md">
          <button
            onClick={decreaseQuantity}
            disabled={quantity <= 1}
            className="px-3 py-2 hover:bg-muted transition-colors disabled:opacity-50"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="px-4 py-2 border-x border-input">{quantity}</span>
          <button
            onClick={increaseQuantity}
            className="px-3 py-2 hover:bg-muted transition-colors"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Add to Cart */}
      <div className="space-y-4">
        <Button onClick={addToCart} className="w-full h-12 text-base" disabled={!product.inStock}>
          <ShoppingBag className="h-5 w-5 mr-2" />
          Add to Cart
        </Button>
        
        <div className="flex items-center text-muted-foreground text-sm">
          <Truck className="h-4 w-4 mr-2" />
          Free shipping on orders over $35
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;
